import React from 'react';
import { CheckCircle, RefreshCw, Upload, Edit3 } from 'lucide-react';

const ResultsSummary = ({ 
  job, 
  getProgressStats, 
  bulkApproveAll, 
  bulkApproveAllAndApply,
  batchApplyMetadata, 
  batchApplying, 
  setShowFirstTimeHelp,
  scrollToFirstPhoto,
  selectedPhotos,
  provider = 'flickr',
  providerName = 'Flickr'
}) => {
  if (!job?.results || job.results.length === 0) return null;

  const stats = getProgressStats();
  const hasSelections = selectedPhotos && selectedPhotos.size > 0;
  
  // Determine if job is still processing using smart detection logic
  const isProcessing = (() => {
    if (!job) return false;
    
    // Explicit status checks
    if (job.status === 'completed' || job.status === 'failed') {
      return false;
    }
    
    // If job status indicates processing
    if (job.status === 'pending' || job.status === 'processing') {
      // But check if all photos are actually done processing
      if (job.results && job.results.length > 0) {
        const pendingPhotos = job.results.filter(r => r.status === 'pending');
        if (pendingPhotos.length === 0) {
          // All photos are done processing (processed, failed, or skipped)
          // Job should be considered completed even if status hasn't been updated
          return false;
        }
      }
      return true;
    }
    
    return false;
  })();
  // Calculate apply completion stats first
  const processedPhotos = job.results.filter(r => r.status === 'processed');
  const appliedPhotos = processedPhotos.filter(r => r.applied);
  const unappliedPhotos = processedPhotos.filter(r => !r.applied);
  
  // Determine completion states
  const hasProcessedPhotos = processedPhotos.length > 0;
  const allProcessedPhotosApplied = hasProcessedPhotos && unappliedPhotos.length === 0;
  
  // Smart applying detection - don't show applying if all photos are actually applied
  const isActuallyApplying = (job.applyProgress?.isApplying || batchApplying) && !allProcessedPhotosApplied;
  
  // Smart completion detection: if all photos are applied, consider it completed even if server isApplying is still true
  const isActuallyCompleted = !isProcessing && allProcessedPhotosApplied;
  const isReadyToApply = !isProcessing && !isActuallyApplying && unappliedPhotos.length > 0;
  const isFullyCompleted = isActuallyCompleted;
  

  // Determine header content based on job status
  const getHeaderContent = () => {
    if (isProcessing) {
      return {
        title: "AI is Processing Your Photos...",
        subtitle: "Please wait while we generate metadata for your photos"
      };
    }
    
    if (isActuallyApplying) {
      return {
        title: `Applying Metadata to ${providerName}...`,
        subtitle: "Your enhanced metadata is being uploaded to your photos"
      };
    }
    
    if (isFullyCompleted) {
      return {
        title: "All Done! Metadata Applied Successfully",
        subtitle: appliedPhotos.length === 1 
          ? `Your photo has been enhanced with AI-generated metadata on ${providerName}`
          : `All ${appliedPhotos.length} photos have been enhanced with AI-generated metadata on ${providerName}`
      };
    }
    
    if (isReadyToApply) {
      return {
        title: "Your AI-Enhanced Photos Are Ready!",
        subtitle: `Review the generated metadata and apply to your ${providerName} photos`
      };
    }
    
    // Default for other states (failed, etc.)
    return {
      title: "Photo Processing Results",
      subtitle: "Review the processing results below"
    };
  };

  const headerContent = getHeaderContent();
  
  // Get provider-specific external link and text
  const getProviderLink = () => {
    switch (provider) {
      case 'flickr':
        return {
          url: 'https://www.flickr.com/photos/me/',
          text: `View Your Photos on ${providerName}`
        };
      case 'unsplash':
        return {
          url: 'https://unsplash.com/collections',
          text: `View Your Collections on ${providerName}`
        };
      case 'smugmug':
        return {
          url: 'https://www.smugmug.com/',
          text: `View Your Photos on ${providerName}`
        };
      default:
        return {
          url: '#',
          text: `View Your Photos on ${providerName}`
        };
    }
  };

  const providerLink = getProviderLink();

  return (
    <div className="mb-6 bg-white rounded-xl shadow-lg p-6">
      <div className="text-center mb-6">
        <div className="flex items-center justify-center mb-2">
          {isProcessing && (
            <RefreshCw className="mr-3 h-6 w-6 animate-spin text-blue-600" />
          )}
          {isActuallyApplying && (
            <Upload className="mr-3 h-6 w-6 text-orange-600" />
          )}
          {(isReadyToApply || isFullyCompleted) && (
            <CheckCircle className="mr-3 h-6 w-6 text-green-600" />
          )}
          <h2 className="text-2xl font-bold text-gray-900">{headerContent.title}</h2>
        </div>
        <p className="text-gray-600">{headerContent.subtitle}</p>
      </div>
      
      {/* Progress Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {isProcessing ? (
          // Show processing progress stats
          <>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {job.results.filter(r => r.status === 'processed').length}
              </div>
              <div className="text-sm text-blue-700 font-medium">Completed</div>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <div className="text-2xl font-bold text-orange-600">
                {job.results.filter(r => r.status === 'pending').length}
              </div>
              <div className="text-sm text-orange-700 font-medium">Processing...</div>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">
                {job.results.filter(r => r.status === 'skipped').length}
              </div>
              <div className="text-sm text-yellow-700 font-medium">Skipped</div>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">
                {job.results.filter(r => r.status === 'failed').length}
              </div>
              <div className="text-sm text-red-700 font-medium">Failed</div>
            </div>
          </>
        ) : isFullyCompleted ? (
          // Show completion stats when all photos are applied
          <>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {appliedPhotos.length}
              </div>
              <div className="text-sm text-green-700 font-medium">Applied Successfully</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {processedPhotos.length}
              </div>
              <div className="text-sm text-blue-700 font-medium">Total Enhanced</div>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">
                {job.results.filter(r => r.status === 'skipped').length}
              </div>
              <div className="text-sm text-yellow-700 font-medium">Skipped</div>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">
                {job.results.filter(r => r.status === 'failed').length}
              </div>
              <div className="text-sm text-red-700 font-medium">Failed</div>
            </div>
          </>
        ) : (
          // Show ready to apply stats
          <>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {unappliedPhotos.length}
              </div>
              <div className="text-sm text-green-700 font-medium">Ready to Apply</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {stats.approved}
              </div>
              <div className="text-sm text-blue-700 font-medium">Approved</div>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">
                {job.results.filter(r => r.status === 'skipped').length}
              </div>
              <div className="text-sm text-yellow-700 font-medium">Skipped</div>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">
                {job.results.filter(r => r.status === 'failed').length}
              </div>
              <div className="text-sm text-red-700 font-medium">Failed</div>
            </div>
          </>
        )}
      </div>

      {/* Processing Progress Bar */}
      {isProcessing && (
        <div className="mb-6 bg-gray-50 rounded-lg p-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">AI Processing Progress</span>
            <span className="text-sm text-gray-600">
              {job.results.filter(r => r.status === 'processed').length} of {job.results.length} completed
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="h-3 bg-gradient-to-r from-blue-500 to-green-500 rounded-full transition-all duration-500"
              style={{ 
                width: `${job.results.length > 0 ? (job.results.filter(r => r.status === 'processed').length / job.results.length) * 100 : 0}%` 
              }}
            ></div>
          </div>
          <p className="text-xs text-gray-500 mt-2 text-center">
            Each photo is being analyzed by AI to generate optimal metadata
          </p>
        </div>
      )}

      {/* Prominent Batch Actions - Only show when not processing and not fully completed */}
      {!isProcessing && !isFullyCompleted && (
        <div className="flex flex-col gap-4 items-center justify-center">
          {hasSelections ? (
            /* Show selected action when user has made choices */
            <div className="flex flex-col md:flex-row gap-4 items-center justify-center w-full">
              <button
                onClick={() => batchApplyMetadata()}
                disabled={batchApplying || isActuallyApplying || selectedPhotos.size === 0}
                className="flex-1 md:flex-none inline-flex items-center justify-center px-8 py-4 bg-blue-600 text-white font-bold text-lg rounded-xl hover:bg-blue-700 hover:scale-105 transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {batchApplying || isActuallyApplying ? (
                  <>
                    <RefreshCw className="mr-3 h-6 w-6 animate-spin" />
                    Applying Selected...
                  </>
                ) : (
                  <>
                    <Upload className="mr-3 h-6 w-6" />
                    Apply Selected to {providerName} ({selectedPhotos.size})
                  </>
                )}
              </button>
            
              <button
                onClick={bulkApproveAllAndApply}
                disabled={batchApplying || isActuallyApplying}
                className="text-gray-600 hover:text-gray-800 font-medium flex items-center border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50"
              >
                <Upload className="mr-2 h-4 w-4" />
                Apply All Instead ({job.results.filter(r => r.status === 'processed' && !r.applied).length})
              </button>
            </div>
          ) : (
            /* Show default actions when no selections */
            <div className="flex flex-col md:flex-row gap-4 items-center justify-center w-full">
              <button
                onClick={bulkApproveAllAndApply}
                disabled={batchApplying || isActuallyApplying || job.results.filter(r => r.status === 'processed' && !r.applied).length === 0}
                className="flex-1 md:flex-none inline-flex items-center justify-center px-8 py-4 bg-green-600 text-white font-bold text-lg rounded-xl hover:bg-green-700 hover:scale-105 transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {batchApplying || isActuallyApplying ? (
                  <>
                    <RefreshCw className="mr-3 h-6 w-6 animate-spin" />
                    Applying All...
                  </>
                ) : (
                  <>
                    <Upload className="mr-3 h-6 w-6" />
                    Apply All to {providerName} ({job.results.filter(r => r.status === 'processed' && !r.applied).length})
                  </>
                )}
              </button>
              
              <button
                onClick={scrollToFirstPhoto}
                className="text-gray-600 hover:text-gray-800 font-medium flex items-center"
              >
                <Edit3 className="mr-2 h-5 w-5" />
                Review Individually First
              </button>
            </div>
          )}
        </div>
      )}

      {/* Completion Actions - Show when all photos are applied */}
      {isFullyCompleted && (
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 text-center max-w-2xl">
            <div className="text-green-600 mb-2">
              <CheckCircle className="mx-auto h-12 w-12" />
            </div>
            <h3 className="text-lg font-semibold text-green-800 mb-2">
              Mission Accomplished! 🎉
            </h3>
            <p className="text-green-700 mb-4">
              {appliedPhotos.length === 1 
                ? `Your photo has been successfully enhanced with AI-generated metadata and is now live on ${providerName}.`
                : `All ${appliedPhotos.length} of your photos have been successfully enhanced with AI-generated metadata and are now live on ${providerName}.`
              }
            </p>
            <div className="flex flex-col md:flex-row gap-3 items-center justify-center">
              <a
                href={providerLink.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                <svg className="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                {providerLink.text}
              </a>
              <button
                onClick={() => window.location.href = '/'}
                className="inline-flex items-center px-6 py-3 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 transition-colors"
              >
                Process More Photos
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Selection Status & Progress Tracker - Hide when fully completed */}
      {!isFullyCompleted && (
        <div className="mt-6 space-y-4">
          {hasSelections && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-blue-600" />
                <div>
                  <h4 className="text-sm font-semibold text-blue-900">
                    {selectedPhotos.size} Photo{selectedPhotos.size !== 1 ? 's' : ''} Selected for Apply
                  </h4>
                  <p className="text-xs text-blue-700">
                    {(() => {
                      const selectedWithExisting = Array.from(selectedPhotos).filter(photoId => {
                        const photo = job.results.find(r => r.photoId === photoId);
                        return photo && (photo.originalTitle || photo.originalDescription);
                      });
                      
                      return selectedWithExisting.length > 0 
                        ? `${selectedWithExisting.length} will replace existing content (backup saved automatically)`
                        : `Ready to send to ${providerName}`;
                    })()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {stats.total > 0 && (
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">
                {hasSelections ? 'Selection Progress' : 'Review Progress'}
              </span>
              <span className="text-sm text-gray-600">
                {hasSelections 
                  ? `${selectedPhotos.size} of ${stats.total} selected` 
                  : `${stats.approved} of ${stats.total} approved`
                }
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${hasSelections ? 'bg-blue-500' : 'bg-green-500'}`}
                style={{ 
                  width: `${stats.total > 0 ? (hasSelections ? (selectedPhotos.size / stats.total) * 100 : (stats.approved / stats.total) * 100) : 0}%` 
                }}
              ></div>
            </div>
          </div>
        )}
        
        {/* Existing Metadata Warning */}
        {(() => {
          const photosWithExistingMetadata = job.results?.filter(r => 
            r.status === 'processed' && 
            !r.applied && 
            (r.originalTitle || r.originalDescription)
          ) || [];
          
          return photosWithExistingMetadata.length > 0 && (
            <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-amber-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-amber-800">Photos with Existing Metadata</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    {photosWithExistingMetadata.length} photo{photosWithExistingMetadata.length !== 1 ? 's have' : ' has'} existing titles or descriptions that will be replaced. 
                    Original metadata will be backed up automatically so you can undo changes.
                  </p>
                  <p className="text-xs text-amber-600 mt-2">
                    💡 After applying, you'll see an "Undo" button to restore original metadata if needed.
                  </p>
                </div>
              </div>
            </div>
          );
        })()}
        </div>
      )}
    </div>
  );
};

export default ResultsSummary;
